import React, { useState, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  Calendar as CalendarIcon, 
  Clock, 
  User, 
  Phone, 
  Check, 
  ChevronRight, 
  ArrowLeft, 
  CheckCircle2, 
  Share2, 
  ShieldCheck, 
  MapPin, 
  MessageCircle, 
  Scissors, 
  CalendarCheck, 
  Users, 
  Copy, 
  ChevronLeft, 
  Navigation, 
  Zap, 
  Wind, 
  Contact, 
  Plus,
  Flame,
  Tag
} from 'lucide-react';
import { Appointment, NicheKey, StorefrontProfile, StaffMember, ServicePromotion } from '../types';
import { formatCurrencyBRL, maskPhone, formatPhone } from '../../../utils/formatUtils';
import { safeStorage } from '../../../utils/safeStorage';
import { downloadIcsFile, generateGoogleCalendarUrl, getLocalDateString } from '../utils/calendarUtils';
import { getDaySchedule, generateDaySlots, generateDynamicDaySlots, parseBusinessHours, timeStringToMinutes } from '../utils/businessHoursUtils';
import { 
  calculateDiscountPercent, 
  calculateDiscountAmount, 
  isPromoActive, 
  getEffectivePrice, 
  getPromoUrgencyText 
} from '../../../utils/promoUtils';
import { filterStaffForServices, isStaffEligibleForService } from '../utils/staffServiceUtils';
import { generateStorefrontTheme } from '../../../utils/colorExtractor';

export interface DirectBookingService {
  id: string;
  name: string;
  category?: string;
  price: number;
  durationMinutes: number;
  description?: string;
  allowedStaffIds?: string[];
  promo?: ServicePromotion;
}

// ─────────────────────────────────────────────────────────────────────────────
// CENÁRIOS DE DEMONSTRAÇÃO 100% COERENTES
// ─────────────────────────────────────────────────────────────────────────────
export interface DemoPreset {
  key: string;
  label: string;
  iconName: 'salao' | 'eletrica' | 'hvac';
  profile: StorefrontProfile;
  staff: StaffMember[];
  services: DirectBookingService[];
}

export const DEMO_PRESETS: Record<string, DemoPreset> = {
  estetica: {
    key: 'estetica',
    label: 'Salão & Estética',
    iconName: 'salao',
    profile: {
      id: 'store_salao',
      userId: 'demo_user',
      slug: 'studio-bella',
      displayName: 'Studio Bella • Cabelo & Estética',
      tagline: 'Alongamento em Gel, Mechas, Escova e Estética Facial',
      bio: 'Salão especializado em beleza feminina com atendimento com hora marcada e equipe certificada.',
      yearsExperience: '8 anos',
      nicheBadges: ['Salão de Beleza', 'Unhas em Gel', 'Mechas & Loiros', 'Estética Facial'],
      whatsappNumber: '11999999999',
      instagramHandle: '@studiobellasp',
      facebookHandle: 'fb.com/studiobellasp',
      tiktokHandle: '@studiobellasp',
      email: 'contato@studiobella.com.br',
      addressDisplay: 'Rua Oscar Freire, 1050 - Jardins, São Paulo - SP',
      accentColor: '#0d9488',
      isPublished: true,
      viewCount: 4120,
      portfolioItems: []
    },
    staff: [
      { id: 'st_any', name: 'Qualquer Profissional', role: 'Primeiro horário livre', color: 'emerald', commissionPercent: 50, active: true },
      { id: 'st_1', name: 'Mariana Silva', role: 'Manicure & Alongamento em Gel', color: 'rose', commissionPercent: 50, active: true, serviceIds: ['s_alongamento', 's_manicure_pedi', 's_blindagem', 's_manutencao_gel'] },
      { id: 'st_2', name: 'Carla Dias', role: 'Cabeleireira & Colorista', color: 'purple', commissionPercent: 60, active: true, serviceIds: ['s_mechas', 's_corte_escova', 's_alisamento', 's_cronograma'] },
      { id: 'st_3', name: 'Letícia Souza', role: 'Esteticista & Sobrancelhas', color: 'amber', commissionPercent: 50, active: true, serviceIds: ['s_limpeza_pele', 's_sobrancelhas', 's_drenagem'] }
    ],
    services: [
      // Unhas (Mariana)
      { 
        id: 's_alongamento', 
        name: 'Alongamento em Gel / Fibra', 
        category: 'Unhas & Manicure', 
        price: 160, 
        durationMinutes: 120, 
        description: 'Aplicação completa com acabamento natural e cutilagem.', 
        allowedStaffIds: ['st_any', 'st_1'],
        promo: {
          active: true,
          promoPrice: 129,
          title: 'Oferta da Semana',
          limitType: 'quantity',
          maxUsage: 10,
          currentUsage: 7
        }
      },
      { id: 's_manicure_pedi', name: 'Manicure + Pedicure Tradicional', category: 'Unhas & Manicure', price: 65, durationMinutes: 60, description: 'Esmaltação impecável e hidratação profunda.', allowedStaffIds: ['st_any', 'st_1'] },
      { id: 's_blindagem', name: 'Blindagem de Unhas Naturais', category: 'Unhas & Manicure', price: 70, durationMinutes: 45, description: 'Camada protetora em gel para unhas que quebram fácil.', allowedStaffIds: ['st_any', 'st_1'] },
      { id: 's_manutencao_gel', name: 'Manutenção de Alongamento em Gel', category: 'Unhas & Manicure', price: 95, durationMinutes: 90, description: 'Reposição de gel e nivelamento.', allowedStaffIds: ['st_any', 'st_1'] },
      // Cabelos (Carla)
      { id: 's_mechas', name: 'Mechas / Morena Iluminada', category: 'Cabelos & Coloração', price: 380, durationMinutes: 180, description: 'Descoloração com tratamento reconstrutor e tonalização.', allowedStaffIds: ['st_any', 'st_2'] },
      { 
        id: 's_corte_escova', 
        name: 'Corte Feminino + Escova Modelada', 
        category: 'Cabelos & Coloração', 
        price: 110, 
        durationMinutes: 60, 
        description: 'Lavagem especial, corte personalizado e finalização.', 
        allowedStaffIds: ['st_any', 'st_2'],
        promo: {
          active: true,
          promoPrice: 89,
          title: 'Quarta Especial',
          limitType: 'none'
        }
      },
      { id: 's_alisamento', name: 'Alisamento Orgânico / Progressiva', category: 'Cabelos & Coloração', price: 220, durationMinutes: 150, description: '100% sem formol com brilho espelhado.', allowedStaffIds: ['st_any', 'st_2'] },
      { id: 's_cronograma', name: 'Cronograma Capilar com Ozônio', category: 'Cabelos & Coloração', price: 130, durationMinutes: 60, description: 'Nutrição e reconstrução com vapor de ozônio.', allowedStaffIds: ['st_any', 'st_2'] },
      // Estética (Letícia)
      { id: 's_limpeza_pele', name: 'Limpeza de Pele Profunda', category: 'Estética Facial', price: 140, durationMinutes: 75, description: 'Extração de cravos, esfoliação e máscara calmante.', allowedStaffIds: ['st_any', 'st_3'] },
      { id: 's_sobrancelhas', name: 'Design de Sobrancelhas com Henna', category: 'Estética Facial', price: 55, durationMinutes: 30, description: 'Alinhamento geométrico e pigmentação natural.', allowedStaffIds: ['st_any', 'st_3'] },
      { id: 's_drenagem', name: 'Drenagem Linfática Corporal', category: 'Estética Facial', price: 120, durationMinutes: 60, description: 'Redução de retenção de líquidos e relaxamento.', allowedStaffIds: ['st_any', 'st_3'] }
    ]
  },

  eletricista: {
    key: 'eletricista',
    label: 'Eletricista (Solo)',
    iconName: 'eletrica',
    profile: {
      id: 'store_eletrica',
      userId: 'demo_user',
      slug: 'fabio-eletricista',
      displayName: 'Fábio Instalações Elétricas',
      tagline: 'Instalações Elétricas, Padrão de Entrada e Iluminação LED',
      bio: 'Eletricista profissional com mais de 11 anos de experiência prestando serviços residenciais e comerciais com garantia e segurança.',
      yearsExperience: '11 anos',
      nicheBadges: ['Instalações Elétricas', 'Padrão Enel', 'Iluminação LED', 'Garantia 90 dias'],
      whatsappNumber: '11999999999',
      instagramHandle: '@fabioeletricistasp',
      facebookHandle: 'fb.com/fabioeletricistasp',
      tiktokHandle: '@fabioeletricistasp',
      email: 'contato@fabioeletricista.com.br',
      addressDisplay: 'Atendimento em Domicílio (São Paulo e Grande SP)',
      accentColor: '#10b981',
      isPublished: true,
      viewCount: 2340,
      portfolioItems: []
    },
    staff: [], // Autônomo solo: sem etapa de escolha de equipe
    services: [
      { id: 's_quadro_qdt', name: 'Montagem / Revisão de Quadro QDT', category: 'Dispositivos & Painéis', price: 350, durationMinutes: 120, description: 'Organização de barramentos, troca de disjuntores e teste de fuga de corrente.' },
      { id: 's_idr_dps', name: 'Instalação de IDR + DPS', category: 'Dispositivos & Painéis', price: 180, durationMinutes: 60, description: 'Proteção essencial contra choques e queima de aparelhos por raios.' },
      { id: 's_chuveiro_circuito', name: 'Circuito Exclusivo para Chuveiro (6mm²)', category: 'Dispositivos & Painéis', price: 180, durationMinutes: 90, description: 'Passagem de cabeamento antichama e disjuntor dimensionado.' },
      { id: 's_perfil_led', name: 'Instalação de Perfil de LED com Fonte', category: 'Iluminação & Pontos', price: 120, durationMinutes: 60, description: 'Corte, fixação, fiação e ligação de fonte chaveada (por perfil).' },
      { id: 's_ponto_tomada', name: 'Ponto Novo de Tomada / Interruptor', category: 'Iluminação & Pontos', price: 55, durationMinutes: 45, description: 'Instalação de caixinha, fiação e espelho novo.' },
      { id: 's_lustre', name: 'Montagem de Lustre / Pendente', category: 'Iluminação & Pontos', price: 120, durationMinutes: 60, description: 'Fixação segura no teto e ligação elétrica.' },
      { id: 's_emergencia', name: 'Visita Técnica e Reparo Rápido', category: 'Manutenção & Vistoria', price: 150, durationMinutes: 60, description: 'Diagnóstico de curto-circuito, disjuntor caindo ou falta de energia.' }
    ]
  },

  hvac: {
    key: 'hvac',
    label: 'Climatização & Split',
    iconName: 'hvac',
    profile: {
      id: 'store_hvac',
      userId: 'demo_user',
      slug: 'polar-climatizacao',
      displayName: 'Polar Climatização & Split',
      tagline: 'Instalação, Higienização Bactericida e Manutenção PMOC',
      bio: 'Empresa especializada em climatização residencial e comercial com técnicos credenciados pelas principais fabricantes.',
      yearsExperience: '9 anos',
      nicheBadges: ['Climatização', 'Instalação Split', 'Higienização', 'PMOC'],
      whatsappNumber: '11999999999',
      instagramHandle: '@polarclimatizacaosp',
      facebookHandle: 'fb.com/polarclimatizacaosp',
      tiktokHandle: '@polarclimatizacaosp',
      email: 'contato@polarclimatizacao.com.br',
      addressDisplay: 'Atendimento em Domicílio (São Paulo e ABC)',
      accentColor: '#0284c7',
      isPublished: true,
      viewCount: 1890,
      portfolioItems: []
    },
    staff: [
      { id: 'st_any', name: 'Qualquer Técnico Disponível', role: 'Primeiro horário livre', color: 'emerald', commissionPercent: 50, active: true },
      { id: 'st_tech1', name: 'Rodrigo Alves', role: 'Técnico Sênior Split', color: 'blue', commissionPercent: 50, active: true },
      { id: 'st_tech2', name: 'Bruno Santos', role: 'Especialista em Inverter & PMOC', color: 'cyan', commissionPercent: 50, active: true }
    ],
    services: [
      { id: 's_split_9_12', name: 'Instalação Split Hi-Wall (9k a 12k BTUs)', category: 'Instalação', price: 450, durationMinutes: 180, description: 'Tubulação de cobre até 2m, furação, suporte e teste de vácuo.' },
      { id: 's_split_18_24', name: 'Instalação Split Inverter (18k a 24k BTUs)', category: 'Instalação', price: 650, durationMinutes: 240, description: 'Instalação completa para ambientes médios/grandes.' },
      { id: 's_higienizacao', name: 'Higienização Completa com Bactericida', category: 'Higienização & Manutenção', price: 160, durationMinutes: 60, description: 'Limpeza de turbina, serpentina, filtros e aplicação de antibactericida.' },
      { id: 's_gas', name: 'Recarga de Gás Ecológico R410a/R32', category: 'Higienização & Manutenção', price: 220, durationMinutes: 60, description: 'Eliminação de microvazamentos e pressurização.' },
      { id: 's_pmoc', name: 'Manutenção Preventiva e Laudo PMOC', category: 'Higienização & Manutenção', price: 280, durationMinutes: 120, description: 'Adequação às normas da Anvisa para escritórios e comércios.' }
    ]
  }
};

interface DirectBookingPageProps {
  profile?: StorefrontProfile;
  nicheKey?: NicheKey;
  services?: DirectBookingService[];
  staffList?: StaffMember[];
  existingAppointments?: Appointment[];
  showDemoSwitcher?: boolean;
  onBookAppointment: (appointment: Appointment) => Promise<void> | void;
  onBack?: () => void;
}

export default function DirectBookingPage({
  profile: initialProfile,
  nicheKey: initialNicheKey = 'estetica',
  services: initialServices,
  staffList: initialStaffList,
  existingAppointments = [],
  showDemoSwitcher = false,
  onBookAppointment,
  onBack
}: DirectBookingPageProps) {
  // Nicho ativo na demonstração
  const [activeDemoKey, setActiveDemoKey] = useState<string>(() => {
    if (initialNicheKey === 'eletricista') return 'eletricista';
    if (initialNicheKey === 'hvac') return 'hvac';
    return 'estetica';
  });

  const activePreset = DEMO_PRESETS[activeDemoKey] || DEMO_PRESETS.estetica;
  const fallbackCleanProfile: StorefrontProfile = {
    id: 'store_clean',
    userId: 'user_clean',
    slug: 'agendamento',
    displayName: 'Agendamento Online',
    tagline: 'Atendimento Profissional',
    bio: '',
    yearsExperience: '',
    nicheBadges: [],
    whatsappNumber: '',
    accentColor: '#10b981',
    isPublished: true,
    viewCount: 0,
    portfolioItems: []
  };
  const currentProfile = initialProfile || (showDemoSwitcher ? activePreset.profile : fallbackCleanProfile);
  const currentStaffList = initialStaffList !== undefined 
    ? initialStaffList 
    : (showDemoSwitcher ? activePreset.staff : []);
  const currentServices = initialServices !== undefined 
    ? initialServices 
    : (showDemoSwitcher ? activePreset.services : []);

  const themeStyles = useMemo(() => {
    return generateStorefrontTheme(currentProfile.accentColor);
  }, [currentProfile.accentColor]);

  const [logoLoadError, setLogoLoadError] = useState(false);
  useEffect(() => {
    setLogoLoadError(false);
  }, [currentProfile.logoUrl]);

  // Steps: 1: Service -> 2: Staff (se houver equipe > 1) -> 3: Date & Time -> 4: Client Info -> 5: Confirmed
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedServices, setSelectedServices] = useState<DirectBookingService[]>([]);
  const selectedService = selectedServices[0] || null;
  const [selectedStaff, setSelectedStaff] = useState<StaffMember>(() => {
    if (currentStaffList.length > 0) return currentStaffList[0];
    return { 
      id: 'st_solo', 
      name: currentProfile.displayName || 'Profissional Responsável', 
      role: currentProfile.tagline || 'Atendente', 
      color: 'emerald', 
      commissionPercent: 100, 
      active: true 
    };
  });
  
  const [selectedDate, setSelectedDate] = useState<string>(() => {
    const today = new Date();
    const todayIso = getLocalDateString(today);
    const sched = getDaySchedule(currentProfile?.businessHours || 'Terça a Sábado: 08:00 às 19:00', todayIso);
    const nowMinutes = today.getHours() * 60 + today.getMinutes();
    const endMinutes = timeStringToMinutes(sched.end || '19:00');

    // Se hoje está aberto e ainda temos tempo no expediente (com buffer de antecedência mínima de 20 min)
    if (sched.isOpen && nowMinutes + 20 < endMinutes) {
      return todayIso;
    }

    // Caso contrário, busca o primeiro dia aberto a partir de amanhã
    for (let i = 1; i <= 14; i++) {
      const next = new Date();
      next.setDate(next.getDate() + i);
      const iso = getLocalDateString(next);
      const nextSched = getDaySchedule(currentProfile?.businessHours || 'Terça a Sábado: 08:00 às 19:00', iso);
      if (nextSched.isOpen) {
        return iso;
      }
    }
    return todayIso;
  });
  const [selectedTime, setSelectedTime] = useState<string>('');
  const savedProfile = useMemo(() => {
    try {
      const raw = safeStorage.getItem('rmax_saved_client_profile');
      if (raw) return JSON.parse(raw);
    } catch {}
    return null;
  }, []);

  const [clientName, setClientName] = useState<string>(() => savedProfile?.name || '');
  const [clientPhone, setClientPhone] = useState<string>(() => savedProfile?.phone || '');
  const [hasAutoFilled, setHasAutoFilled] = useState<boolean>(() => Boolean(savedProfile?.name && savedProfile?.phone));
  const [supportsContactPicker, setSupportsContactPicker] = useState<boolean>(false);
  const [clientNotes, setClientNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createdAppointment, setCreatedAppointment] = useState<Appointment | null>(null);
  const [copiedLink, setCopiedLink] = useState(false);

  // Cálculos acumulados de múltiplos serviços com suporte a ofertas e promoções
  const totalPrice = useMemo(() => {
    return selectedServices.reduce((sum, s) => {
      const effective = getEffectivePrice(s.price, s.promo, selectedDate);
      return sum + effective;
    }, 0);
  }, [selectedServices, selectedDate]);

  const originalTotalPrice = useMemo(() => {
    return selectedServices.reduce((sum, s) => sum + s.price, 0);
  }, [selectedServices]);

  const totalDiscount = Math.max(0, originalTotalPrice - totalPrice);

  const totalDurationMinutes = useMemo(() => {
    return selectedServices.reduce((sum, s) => sum + (s.durationMinutes || 60), 0);
  }, [selectedServices]);

  const formatDurationDisplay = (totalMins: number) => {
    if (totalMins < 60) return `${totalMins}min`;
    const h = Math.floor(totalMins / 60);
    const m = totalMins % 60;
    if (m === 0) return `${h}h`;
    return `${h}h ${m}min`;
  };

  const handleToggleService = (service: DirectBookingService) => {
    setSelectedServices(prev => {
      const exists = prev.some(s => s.id === service.id);
      if (exists) {
        return prev.filter(s => s.id !== service.id);
      } else {
        return [...prev, service];
      }
    });
  };

  useEffect(() => {
    if (typeof window !== 'undefined' && 'contacts' in navigator && 'ContactsManager' in window) {
      setSupportsContactPicker(true);
    }
  }, []);

  const handlePickContact = async () => {
    try {
      const contacts = await (navigator as any).contacts.select(['name', 'tel'], { multiple: false });
      if (contacts && contacts.length > 0) {
        const contact = contacts[0];
        if (contact.name && contact.name[0]) {
          setClientName(contact.name[0]);
        }
        if (contact.tel && contact.tel[0]) {
          setClientPhone(maskPhone(contact.tel[0]));
        }
        setHasAutoFilled(true);
      }
    } catch (err) {
      console.log('Contact selection cancelled or unavailable:', err);
    }
  };

  const handleClearAutoFill = () => {
    setClientName('');
    setClientPhone('');
    setHasAutoFilled(false);
    safeStorage.removeItem('rmax_saved_client_profile');
  };

  // Reinicia estados ao trocar de nicho na demo
  const handleSwitchDemo = (key: string) => {
    setActiveDemoKey(key);
    setCurrentStep(1);
    setSelectedCategory('all');
    setSelectedServices([]);
    setSelectedTime('');
    setCreatedAppointment(null);
  };

  // Atualiza selectedStaff quando muda o staff list
  useEffect(() => {
    if (currentStaffList.length > 0) {
      setSelectedStaff(currentStaffList[0]);
    }
  }, [activeDemoKey, currentStaffList]);

  // Categorias únicas do nicho ativo
  const categories = useMemo(() => {
    const set = new Set<string>();
    currentServices.forEach(s => {
      if (s.category) set.add(s.category);
    });
    return Array.from(set);
  }, [currentServices]);

  const filteredServices = useMemo(() => {
    if (selectedCategory === 'all') return currentServices;
    return currentServices.filter(s => s.category === selectedCategory);
  }, [currentServices, selectedCategory]);

  // Filtra atendentes habilitados para os procedimentos selecionados
  const availableStaffForService = useMemo(() => {
    return filterStaffForServices(currentStaffList, selectedServices);
  }, [selectedServices, currentStaffList]);

  // Se o profissional selecionado não estiver mais elegível para os serviços escolhidos, reseta para o primeiro disponível
  useEffect(() => {
    if (availableStaffForService.length > 0 && !availableStaffForService.some(s => s.id === selectedStaff.id)) {
      setSelectedStaff(availableStaffForService[0]);
    }
  }, [availableStaffForService, selectedStaff.id]);

  const hasMultipleStaff = currentStaffList.length > 1;

  // Alias para conversão de tempo
  const timeToMinutes = timeStringToMinutes;

  // Próximos 14 dias para agendamento com verificação de expediente do salão (incluindo Hoje)
  const availableDates = useMemo(() => {
    const todayIso = getLocalDateString();
    const now = new Date();
    const currentMinutes = now.getHours() * 60 + now.getMinutes();
    const advanceNoticeBufferMinutes = 20;

    return Array.from({ length: 14 }, (_, i) => {
      const d = new Date();
      d.setDate(d.getDate() + i);
      const iso = getLocalDateString(d);
      const isToday = iso === todayIso;
      const daySched = getDaySchedule(currentProfile.businessHours, iso);

      let isOpen = daySched.isOpen;
      if (isToday && isOpen) {
        // Se hoje já passou do expediente restante (com margem de antecedência), marca como fechado/esgotado
        const dayEndMinutes = timeToMinutes(daySched.end || '19:00');
        if (currentMinutes + advanceNoticeBufferMinutes >= dayEndMinutes) {
          isOpen = false;
        }
      }

      return {
        iso,
        dayOfWeek: d.toLocaleDateString('pt-BR', { weekday: 'short' }).replace('.', ''),
        dayNumber: d.getDate(),
        month: d.toLocaleDateString('pt-BR', { month: 'short' }).replace('.', ''),
        isOpen,
        isToday
      };
    });
  }, [currentProfile.businessHours]);

  const currentDaySchedule = useMemo(() => {
    return getDaySchedule(currentProfile.businessHours, selectedDate);
  }, [currentProfile.businessHours, selectedDate]);

  const standardTimeSlots = useMemo(() => {
    return generateDynamicDaySlots(currentProfile.businessHours, selectedDate, existingAppointments, 30);
  }, [currentProfile.businessHours, selectedDate, existingAppointments]);

  // Horários livres considerando agendamentos existentes e janela contínua da duração total
  const freeTimeSlots = useMemo(() => {
    if (!currentDaySchedule.isOpen || standardTimeSlots.length === 0) {
      return [];
    }

    const todayIso = getLocalDateString();
    const isToday = selectedDate === todayIso;
    const now = new Date();
    const currentMinutes = now.getHours() * 60 + now.getMinutes();
    const advanceNoticeBufferMinutes = 20;

    const requiredDuration = totalDurationMinutes > 0 ? totalDurationMinutes : 60;
    const dayEndMinutes = timeToMinutes(currentDaySchedule.end);
    const eligibleStaffList = availableStaffForService.filter(st => st.id !== 'st_any');

    return standardTimeSlots.filter(slot => {
      const slotStart = timeToMinutes(slot);
      const slotEnd = slotStart + requiredDuration;

      // Se a data selecionada for hoje, ignora horários passados ou dentro da margem de antecedência
      if (isToday && slotStart < currentMinutes + advanceNoticeBufferMinutes) {
        return false;
      }

      if (slotEnd > dayEndMinutes) return false;

      // Função auxiliar: verifica se um profissional específico está ocupado no intervalo
      const isStaffBusy = (staffId: string) => {
        return existingAppointments.some(a => {
          if (a.scheduledDate !== selectedDate || a.status === 'canceled') return false;
          if (a.staffMemberId && a.staffMemberId !== staffId) return false;

          // Se for bloqueio ou agendamento de dia inteiro
          if (a.allDay || (a.durationMinutes && a.durationMinutes >= 720)) return true;

          const aStart = timeToMinutes(a.scheduledTime);
          const aEnd = aStart + (a.durationMinutes || 60);

          return Math.max(slotStart, aStart) < Math.min(slotEnd, aEnd);
        });
      };

      if (selectedStaff.id !== 'st_any') {
        // Profissional específico selecionado: conflito apenas se ele mesmo estiver ocupado
        return !isStaffBusy(selectedStaff.id);
      } else {
        // "Qualquer Profissional" selecionado:
        // Se houver profissionais elegíveis para o serviço, o slot está livre se PELO MENOS UM estiver livre
        if (eligibleStaffList.length > 0) {
          return eligibleStaffList.some(st => !isStaffBusy(st.id));
        }

        // Caso não haja lista de equipe (atendimento solo), verifica colisão geral
        const hasConflict = existingAppointments.some(a => {
          if (a.scheduledDate !== selectedDate || a.status === 'canceled') return false;
          if (a.allDay || (a.durationMinutes && a.durationMinutes >= 720)) return true;
          const aStart = timeToMinutes(a.scheduledTime);
          const aEnd = aStart + (a.durationMinutes || 60);
          return Math.max(slotStart, aStart) < Math.min(slotEnd, aEnd);
        });

        return !hasConflict;
      }
    });
  }, [currentDaySchedule, standardTimeSlots, existingAppointments, selectedDate, selectedStaff, totalDurationMinutes, availableStaffForService]);

  useEffect(() => {
    if (selectedTime && !freeTimeSlots.includes(selectedTime)) {
      setSelectedTime('');
    }
  }, [freeTimeSlots, selectedTime]);

  // Formatação de telefone
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setClientPhone(maskPhone(e.target.value));
  };

  const handleConfirmBooking = async (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedServices.length === 0 || !selectedDate || !selectedTime || !clientName.trim() || !clientPhone.trim()) {
      alert('Por favor, selecione os serviços e preencha seu nome e telefone.');
      return;
    }

    setIsSubmitting(true);
    const isSolo = !hasMultipleStaff;
    const combinedTitle = selectedServices.map(s => s.name).join(' + ');

    // Atribuição de profissional e blindagem contra agendamento concorrente simultâneo:
    const slotStart = timeToMinutes(selectedTime);
    const slotEnd = slotStart + (totalDurationMinutes || 60);

    const checkStaffConflict = (staffId?: string) => {
      return existingAppointments.some(a => {
        if (a.scheduledDate !== selectedDate || a.status === 'canceled') return false;
        if (staffId && a.staffMemberId && a.staffMemberId !== staffId) return false;
        if (a.allDay || (a.durationMinutes && a.durationMinutes >= 720)) return true;
        const aStart = timeToMinutes(a.scheduledTime);
        const aEnd = aStart + (a.durationMinutes || 60);
        return Math.max(slotStart, aStart) < Math.min(slotEnd, aEnd);
      });
    };

    let assignedStaffId = (!isSolo && selectedStaff.id !== 'st_any') ? selectedStaff.id : undefined;
    let assignedStaffName = (!isSolo && selectedStaff.id !== 'st_any') ? selectedStaff.name : undefined;

    if (!isSolo && selectedStaff.id !== 'st_any') {
      if (checkStaffConflict(selectedStaff.id)) {
        setIsSubmitting(false);
        alert(`Atenção: O horário das ${selectedTime} com ${selectedStaff.name} acabou de ser preenchido por outro agendamento.\n\nPor favor, escolha outro horário disponível.`);
        setSelectedTime('');
        return;
      }
    } else if (!isSolo && selectedStaff.id === 'st_any') {
      const eligibleStaffList = availableStaffForService.filter(st => st.id !== 'st_any');
      const freeStaff = eligibleStaffList.find(st => !checkStaffConflict(st.id));

      if (!freeStaff) {
        setIsSubmitting(false);
        alert(`Atenção: Todos os profissionais para as ${selectedTime} acabaram de ser ocupados.\n\nPor favor, escolha outro horário disponível.`);
        setSelectedTime('');
        return;
      }

      assignedStaffId = freeStaff.id;
      assignedStaffName = freeStaff.name;
    } else {
      if (checkStaffConflict()) {
        setIsSubmitting(false);
        alert(`Atenção: O horário das ${selectedTime} acabou de ser reservado.\n\nPor favor, selecione outro horário disponível.`);
        setSelectedTime('');
        return;
      }
    }


    const newAppointment: Appointment = {
      id: 'apt_' + Date.now().toString(36),
      userId: currentProfile.userId || 'user_main',
      clientName: clientName.trim(),
      clientPhone: clientPhone.trim(),
      serviceName: combinedTitle,
      servicesList: selectedServices.map(s => ({
        id: s.id,
        name: s.name,
        price: getEffectivePrice(s.price, s.promo, selectedDate),
        durationMinutes: s.durationMinutes,
        category: s.category
      })),
      staffMemberId: assignedStaffId,
      staffMemberName: assignedStaffName,
      durationMinutes: totalDurationMinutes || 60,
      scheduledDate: selectedDate,
      scheduledTime: selectedTime,
      price: totalPrice,
      status: 'confirmed',
      notes: clientNotes.trim() ? `[Link Direto Multi-Serviço] ${clientNotes.trim()}` : '[Link Direto]',
      historyTimeline: [
        {
          id: 'h_' + Date.now(),
          timestamp: new Date().toISOString(),
          type: 'bot_booking',
          text: `Agendamento realizado via Link Direto (${selectedServices.length} procedimentos).`
        }
      ],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    try {
      // Salva os dados de contato localmente para agendamentos futuros em 1 toque
      try {
        safeStorage.setItem('rmax_saved_client_profile', JSON.stringify({
          name: clientName.trim(),
          phone: clientPhone.trim()
        }));
      } catch {}

      await onBookAppointment(newAppointment);
      setCreatedAppointment(newAppointment);
      setCurrentStep(5); // Tela de Sucesso
    } catch (err) {
      console.error('Erro ao salvar agendamento:', err);
      alert('Houve um erro ao processar seu agendamento. Tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCopyDirectLink = () => {
    const link = `${window.location.origin}/agendar/${currentProfile.slug}`;
    navigator.clipboard.writeText(link);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2500);
  };

  const handleAddToCalendar = (mode: 'apple_ics' | 'google' = 'apple_ics') => {
    if (!createdAppointment) return;

    const servicesDesc = createdAppointment.servicesList && createdAppointment.servicesList.length > 0
      ? createdAppointment.servicesList.map(s => `• ${s.name} (${s.durationMinutes}min - ${formatCurrencyBRL(s.price)})`).join('\n')
      : createdAppointment.serviceName;

    const details = `Agendamento confirmado com ${currentProfile.displayName}\n\nProcedimentos:\n${servicesDesc}\n\nValor Total: ${formatCurrencyBRL(createdAppointment.price)}\n\n⏰ Alarme configurado automaticamente para as 08:00 da manhã do dia do atendimento.`;

    const title = `${createdAppointment.serviceName} - ${currentProfile.displayName}`;

    if (mode === 'google') {
      const gCalUrl = generateGoogleCalendarUrl({
        title,
        description: details,
        location: currentProfile.addressDisplay || '',
        scheduledDate: createdAppointment.scheduledDate,
        scheduledTime: createdAppointment.scheduledTime,
        durationMinutes: createdAppointment.durationMinutes || 60,
        providerName: currentProfile.displayName
      });
      window.open(gCalUrl, '_blank');
    } else {
      // Baixa arquivo .ics com alarme prioritário às 08:00 da manhã + 45 min antes
      downloadIcsFile({
        title,
        description: details,
        location: currentProfile.addressDisplay || '',
        scheduledDate: createdAppointment.scheduledDate,
        scheduledTime: createdAppointment.scheduledTime,
        durationMinutes: createdAppointment.durationMinutes || 60,
        providerName: currentProfile.displayName,
        clientName: createdAppointment.clientName
      }, `agendamento-${createdAppointment.scheduledDate}.ics`);
    }
  };

  const totalSteps = hasMultipleStaff ? 4 : 3;
  const currentStepNumber = currentStep === 1 
    ? 1 
    : currentStep === 2 
    ? 2 
    : currentStep === 3 
    ? (hasMultipleStaff ? 3 : 2) 
    : (hasMultipleStaff ? 4 : 3);

  return (
    <div style={themeStyles} className="min-h-screen bg-zinc-50 text-zinc-900 flex flex-col justify-between font-sans selection:bg-emerald-100">
      
      {/* Barra de Seleção de Demonstração (Para Apresentação a Clientes) */}
      {showDemoSwitcher && (
        <div className="bg-neutral-950 text-white px-3 py-2 border-b border-neutral-800 text-xs shadow-xs">
          <div className="max-w-xl mx-auto flex flex-wrap items-center justify-between gap-2">
            <span className="text-[10px] font-black uppercase tracking-widest text-zinc-400">
              Alternar Demonstração:
            </span>
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => handleSwitchDemo('estetica')}
                className={`px-3 py-1 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 active:scale-95 ${
                  activeDemoKey === 'estetica'
                    ? 'bg-brand text-white shadow-xs'
                    : 'text-zinc-300 hover:text-white hover:bg-zinc-800'
                }`}
              >
                <Scissors className="h-3.5 w-3.5" />
                <span>Salão Studio Bella</span>
              </button>

              <button
                type="button"
                onClick={() => handleSwitchDemo('eletricista')}
                className={`px-3 py-1 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 active:scale-95 ${
                  activeDemoKey === 'eletricista'
                    ? 'bg-brand text-white shadow-xs'
                    : 'text-zinc-300 hover:text-white hover:bg-zinc-800'
                }`}
              >
                <Zap className="h-3.5 w-3.5" />
                <span>Fábio Eletricista</span>
              </button>

              <button
                type="button"
                onClick={() => handleSwitchDemo('hvac')}
                className={`px-3 py-1 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 active:scale-95 ${
                  activeDemoKey === 'hvac'
                    ? 'bg-brand text-white shadow-xs'
                    : 'text-zinc-300 hover:text-white hover:bg-zinc-800'
                }`}
              >
                <Wind className="h-3.5 w-3.5" />
                <span>Polar Climatização</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Aviso Sóbrio de Modo Demonstração */}
      {(showDemoSwitcher || currentProfile.userId === 'demo_user') && (
        <div className="bg-amber-50 border-b border-amber-200/80 px-4 py-2 text-center text-xs font-medium text-amber-900 flex items-center justify-center gap-2">
          <span>⚠️</span>
          <span>
            <strong>Página em Modo de Demonstração:</strong> Os procedimentos e horários simulados aqui não alteram a agenda do estabelecimento.
          </span>
        </div>
      )}

      {/* Top Header Minimalista e Claro */}
      <header className="bg-white/95 backdrop-blur-md border-b border-zinc-100 sticky top-0 z-30 px-4 py-3.5 shadow-xs">
        <div className="max-w-xl mx-auto flex items-center justify-between">
          {onBack ? (
            <button
              onClick={onBack}
              className="flex items-center gap-1.5 text-xs font-bold text-zinc-700 hover:text-zinc-900 transition-colors cursor-pointer px-2.5 py-1.5 rounded-xl hover:bg-zinc-100"
            >
              <ArrowLeft className="h-4 w-4 text-zinc-500" />
              <span>Voltar ao Painel</span>
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <span className="h-2.5 w-2.5 rounded-full bg-brand animate-pulse" />
              <span className="text-xs font-black text-brand uppercase tracking-wider">
                Agendamento Online
              </span>
            </div>
          )}

          <button
            onClick={handleCopyDirectLink}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 hover:bg-emerald-100 text-brand text-xs font-bold rounded-xl border border-emerald-100 transition-all cursor-pointer shadow-xs active:scale-95"
            title="Copiar link direto"
          >
            {copiedLink ? (
              <>
                <Check className="h-3.5 w-3.5 text-brand" />
                <span className="text-brand font-bold">Link Copiado!</span>
              </>
            ) : (
              <>
                <Share2 className="h-3.5 w-3.5 text-brand" />
                <span>Compartilhar</span>
              </>
            )}
          </button>
        </div>
      </header>

      {/* Conteúdo Principal */}
      <main className="max-w-xl w-full mx-auto p-4 sm:p-6 flex-1 space-y-5">
        
        {/* Card do Estabelecimento / Perfil Limpo */}
        <div className="bg-white border border-zinc-100 rounded-2xl p-4 sm:p-5 shadow-sm">
          <div className="flex items-center gap-3.5">
            {currentProfile.logoUrl && !logoLoadError ? (
              <img
                src={currentProfile.logoUrl}
                alt={currentProfile.displayName}
                onError={() => setLogoLoadError(true)}
                className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl object-cover border border-zinc-200 shadow-xs shrink-0"
              />
            ) : (
              <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-brand/10 text-brand font-black text-xl flex items-center justify-center border border-brand/20 shadow-xs shrink-0 overflow-hidden">
                {(currentProfile.displayName || 'RM').slice(0, 2).toUpperCase()}
              </div>
            )}

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-base sm:text-lg font-bold text-zinc-900 truncate">
                  {currentProfile.displayName}
                </h1>
                <span className="inline-flex items-center gap-1 text-[10px] font-black text-brand bg-emerald-50 border border-emerald-100 px-2 py-0.5 rounded-full">
                  <ShieldCheck className="h-3 w-3 text-brand" /> Verificado
                </span>
              </div>

              <p className="text-xs text-zinc-500 mt-0.5 line-clamp-1 font-medium">
                {currentProfile.tagline || currentProfile.bio || 'Atendimento Profissional'}
              </p>

              {currentProfile.addressDisplay && (
                <div className="flex items-center gap-1 text-xs text-zinc-400 mt-1 font-medium">
                  <MapPin className="h-3.5 w-3.5 text-zinc-400 shrink-0" />
                  <span className="truncate">{currentProfile.addressDisplay}</span>
                </div>
              )}

              <div className="flex items-center gap-1.5 text-[11px] text-zinc-600 mt-1.5 font-medium bg-zinc-50 border border-zinc-100 px-2.5 py-1 rounded-xl w-fit">
                <Clock className="h-3.5 w-3.5 text-brand shrink-0" />
                <span>{currentProfile.businessHours || 'Terça a Sábado: 08:00 às 19:00'}</span>
              </div>
            </div>
          </div>
        </div>

        {/* ────────────────────────────────────────────────────────────────── */}
        {/* PASSO 1: PROCEDIMENTOS / SERVIÇOS (SELEÇÃO MÚLTIPLA)               */}
        {/* ────────────────────────────────────────────────────────────────── */}
        {currentStep === 1 && (
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-3 pb-24"
          >
            {/* Categorias em Grade Visível (Grid 2 colunas para mobile) */}
            {categories.length > 1 && (
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setSelectedCategory('all')}
                  className={`px-3 py-2 rounded-xl text-xs text-center transition-all cursor-pointer truncate ${
                    selectedCategory === 'all'
                      ? 'bg-brand text-white font-bold shadow-xs'
                      : 'bg-white text-zinc-700 hover:bg-zinc-50 border border-zinc-100 font-medium'
                  }`}
                >
                  Todos os Serviços
                </button>
                {categories.map(cat => (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => setSelectedCategory(cat)}
                    className={`px-3 py-2 rounded-xl text-xs text-center transition-all cursor-pointer truncate ${
                      selectedCategory === cat
                        ? 'bg-brand text-white font-bold shadow-xs'
                        : 'bg-white text-zinc-700 hover:bg-zinc-50 border border-zinc-100 font-medium'
                    }`}
                    title={cat}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            )}

            {/* Empty State se não houver serviços */}
            {filteredServices.length === 0 && (
              <div className="bg-white border border-zinc-100 rounded-2xl p-8 text-center space-y-4 shadow-sm">
                <div className="w-12 h-12 rounded-2xl bg-zinc-100 text-zinc-400 flex items-center justify-center mx-auto">
                  <CalendarIcon className="h-6 w-6" />
                </div>
                <div className="space-y-1">
                  <h3 className="text-sm font-bold text-zinc-900">Nenhum serviço disponível no momento</h3>
                  <p className="text-xs text-zinc-500 max-w-xs mx-auto">
                    Entre em contato diretamente com o estabelecimento para consultar a tabela de serviços e horários.
                  </p>
                </div>
                {currentProfile.whatsappNumber && (
                  <button
                    type="button"
                    onClick={() => {
                      const cleanPhone = currentProfile.whatsappNumber.replace(/\D/g, '');
                      const msg = `Olá! Gostaria de consultar serviços e agendamentos com *${currentProfile.displayName}*.`;
                      window.open(`https://api.whatsapp.com/send?phone=55${cleanPhone}&text=${encodeURIComponent(msg)}`, '_blank');
                    }}
                    className="inline-flex items-center gap-2 px-4 py-2.5 bg-brand hover:bg-brand-dark text-white font-bold text-xs rounded-xl shadow-xs transition-all cursor-pointer"
                  >
                    <MessageCircle className="h-4 w-4" />
                    <span>Falar no WhatsApp</span>
                  </button>
                )}
              </div>
            )}

            {/* Lista de Cards de Serviços com Seleção Múltipla */}
            <div className="space-y-2.5">
              {filteredServices.map(service => {
                const isSelected = selectedServices.some(s => s.id === service.id);
                const hasPromo = isPromoActive(service.promo, selectedDate);
                const effectivePrice = getEffectivePrice(service.price, service.promo, selectedDate);
                const discountPercent = hasPromo ? calculateDiscountPercent(service.price, service.promo!.promoPrice) : 0;
                const urgencyText = hasPromo ? getPromoUrgencyText(service.promo) : null;

                return (
                  <div
                    key={service.id}
                    onClick={() => handleToggleService(service)}
                    className={`p-4 rounded-2xl border transition-all cursor-pointer flex items-center justify-between gap-3 active:scale-98 ${
                      isSelected
                        ? 'bg-emerald-50/70 border-emerald-500 ring-2 ring-emerald-500/20 shadow-xs'
                        : hasPromo
                        ? 'bg-amber-50/20 border-amber-300 hover:border-amber-400 hover:shadow-sm'
                        : 'bg-white border-zinc-100 hover:border-zinc-200 hover:shadow-sm'
                    }`}
                  >
                    <div className="space-y-1 min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className={`text-sm font-bold truncate ${isSelected ? 'text-brand' : 'text-zinc-900'}`}>
                          {service.name}
                        </h3>
                        {isSelected && (
                          <span className="text-[9px] font-black uppercase bg-brand text-white px-2 py-0.5 rounded-full">
                            Selecionado
                          </span>
                        )}
                        {hasPromo && (
                          <span className="text-[9px] font-black uppercase bg-linear-to-r from-amber-500 to-orange-500 text-white px-2 py-0.5 rounded-full shadow-xs flex items-center gap-1">
                            <Flame className="h-3 w-3" />
                            {service.promo?.title ? `${service.promo.title} • ` : ''}{discountPercent}% OFF
                          </span>
                        )}
                      </div>

                      {service.description && (
                        <p className="text-xs text-zinc-500 line-clamp-1 font-medium">
                          {service.description}
                        </p>
                      )}

                      <div className="flex flex-wrap items-center gap-2 text-xs text-zinc-500 pt-0.5">
                        <span className="flex items-center gap-1 text-zinc-400 font-medium">
                          <Clock className="h-3.5 w-3.5 text-zinc-400" />
                          {service.durationMinutes} min
                        </span>
                        <span>•</span>
                        {hasPromo ? (
                          <div className="flex items-center gap-1.5">
                            <span className="line-through text-zinc-400 font-medium text-xs">
                              {formatCurrencyBRL(service.price)}
                            </span>
                            <span className="font-black text-amber-600 text-sm">
                              {formatCurrencyBRL(effectivePrice)}
                            </span>
                          </div>
                        ) : (
                          <span className="font-black text-brand text-sm">
                            {formatCurrencyBRL(service.price)}
                          </span>
                        )}
                        {urgencyText && (
                          <span className="text-[10px] font-bold text-amber-700 bg-amber-100/80 px-2 py-0.5 rounded-md">
                            {urgencyText}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0 transition-all ${
                      isSelected 
                        ? 'bg-brand text-white shadow-xs' 
                        : hasPromo
                        ? 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                        : 'bg-zinc-100 text-zinc-400 hover:bg-zinc-200'
                    }`}>
                      {isSelected ? <Check className="h-4 w-4 stroke-3" /> : <Plus className="h-4 w-4" />}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Barra Flutuante Tátil no Rodapé (Sticky Cart) */}
            {selectedServices.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="fixed bottom-4 left-4 right-4 max-w-lg mx-auto z-40 bg-zinc-900/95 backdrop-blur-md text-white p-3.5 rounded-2xl shadow-2xl border border-zinc-800 flex items-center justify-between gap-3"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-black">
                      {selectedServices.length} {selectedServices.length === 1 ? 'procedimento' : 'procedimentos'}
                    </span>
                    <span className="text-[10px] bg-white/20 px-2 py-0.5 rounded-full font-bold">
                      ⏱️ {formatDurationDisplay(totalDurationMinutes)}
                    </span>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <strong className="text-sm font-black text-emerald-400">
                      {formatCurrencyBRL(totalPrice)}
                    </strong>
                    {totalDiscount > 0 && (
                      <span className="text-[10px] text-amber-300 font-bold">
                        (Economia de {formatCurrencyBRL(totalDiscount)})
                      </span>
                    )}
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    const staffCount = currentStaffList.length;
                    setCurrentStep(staffCount > 1 ? 2 : 3);
                  }}
                  className="px-4 py-2.5 bg-brand hover:bg-brand-dark text-white font-black text-xs uppercase tracking-wider rounded-xl shadow-sm shadow-brand/20 flex items-center gap-1.5 cursor-pointer active:scale-95 transition-all shrink-0"
                >
                  <span>Continuar</span>
                  <ChevronRight className="h-4 w-4" />
                </button>
              </motion.div>
            )}
          </motion.div>
        )}

        {/* ────────────────────────────────────────────────────────────────── */}
        {/* PASSO 2: PROFISSIONAL / ATENDENTE (SE HOUVER EQUIPE)              */}
        {/* ────────────────────────────────────────────────────────────────── */}
        {currentStep === 2 && hasMultipleStaff && (
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-3.5"
          >
            <div className="flex items-center justify-between">
              <button
                onClick={() => setCurrentStep(1)}
                className="text-xs font-bold text-zinc-500 hover:text-zinc-900 flex items-center gap-1 cursor-pointer"
              >
                <ChevronLeft className="h-4 w-4" /> Voltar
              </button>
              <h2 className="text-sm font-bold text-zinc-900">
                Escolha o Profissional
              </h2>
            </div>

            {/* Resumo dos serviços selecionados */}
            <div className="p-3 bg-white border border-zinc-100 rounded-2xl space-y-1.5 text-xs text-zinc-700 shadow-xs">
              <div className="flex items-center justify-between pb-1.5 border-b border-zinc-100">
                <span className="text-[10px] text-zinc-400 uppercase font-black tracking-wider">
                  {selectedServices.length} {selectedServices.length === 1 ? 'Serviço Selecionado' : 'Serviços Selecionados'} ({formatDurationDisplay(totalDurationMinutes)})
                </span>
                <span className="font-black text-brand text-sm">
                  {formatCurrencyBRL(totalPrice)}
                </span>
              </div>
              <p className="font-bold text-zinc-900 line-clamp-1">
                {selectedServices.map(s => s.name).join(', ')}
              </p>
            </div>

            <div className="space-y-2.5">
              {availableStaffForService.map(st => {
                const isSelected = selectedStaff.id === st.id;
                return (
                  <div
                    key={st.id}
                    onClick={() => {
                      setSelectedStaff(st);
                      setCurrentStep(3);
                    }}
                    className={`p-3.5 rounded-2xl border transition-all cursor-pointer flex items-center justify-between gap-3 active:scale-98 ${
                      isSelected
                        ? 'bg-emerald-50/60 border-emerald-500 ring-2 ring-emerald-500/20 shadow-xs'
                        : 'bg-white border-zinc-100 hover:border-zinc-200'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-11 h-11 rounded-2xl flex items-center justify-center text-xs font-bold border ${
                        st.id === 'st_any' 
                          ? 'bg-zinc-100 text-zinc-700 border-zinc-200' 
                          : 'bg-brand/10 text-brand-dark border-brand/20'
                      }`}>
                        {st.id === 'st_any' ? '⚡' : st.name.slice(0, 2).toUpperCase()}
                      </div>
                      <div>
                        <h3 className="text-sm font-bold text-zinc-900">{st.name}</h3>
                        <p className="text-xs text-zinc-500 font-medium">{st.role}</p>
                      </div>
                    </div>

                    <div className="p-2 rounded-xl bg-zinc-50 text-zinc-400 border border-zinc-100">
                      <ChevronRight className="h-4 w-4" />
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* ────────────────────────────────────────────────────────────────── */}
        {/* PASSO 3: DATA & HORÁRIO                                           */}
        {/* ────────────────────────────────────────────────────────────────── */}
        {currentStep === 3 && (
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-4"
          >
            <div className="flex items-center justify-between">
              <button
                onClick={() => setCurrentStep(hasMultipleStaff ? 2 : 1)}
                className="text-xs font-bold text-zinc-500 hover:text-zinc-900 flex items-center gap-1 cursor-pointer"
              >
                <ChevronLeft className="h-4 w-4" /> Voltar
              </button>
              <h2 className="text-sm font-bold text-zinc-900">
                Escolha Dia & Horário
              </h2>
            </div>

            {/* Resumo dos Serviços Escolhidos */}
            <div className="p-3 bg-white border border-zinc-100 rounded-2xl space-y-1.5 text-xs text-zinc-700 shadow-xs">
              <div className="flex items-center justify-between pb-1 border-b border-zinc-100">
                <span className="font-bold text-zinc-900">
                  {selectedServices.length} {selectedServices.length === 1 ? 'Procedimento' : 'Procedimentos'} • {formatDurationDisplay(totalDurationMinutes)}
                </span>
                <span className="font-black text-brand text-sm">
                  {formatCurrencyBRL(totalPrice)}
                </span>
              </div>
              <p className="text-[11px] text-zinc-500 truncate">
                {selectedServices.map(s => s.name).join(' + ')}
              </p>
              {hasMultipleStaff && selectedStaff.id !== 'st_any' && (
                <p className="text-[10px] text-brand font-bold">
                  Profissional: {selectedStaff.name}
                </p>
              )}
            </div>

            {/* Horário de Funcionamento */}
            <div className="flex items-center gap-2 text-xs text-zinc-700 bg-white border border-zinc-100 p-3 rounded-2xl shadow-2xs font-medium">
              <Clock className="h-4 w-4 text-brand shrink-0" />
              <span>Horário de Atendimento: <strong>{parseBusinessHours(currentProfile.businessHours).summary}</strong></span>
            </div>

            {/* Carrossel de Dias */}
            <div>
              <label className="text-[10px] text-zinc-400 uppercase font-black tracking-widest mb-2 block">
                Selecione o Dia:
              </label>
              <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-none">
                {availableDates.map(d => {
                  const isSelected = selectedDate === d.iso;
                  return (
                    <button
                      key={d.iso}
                      type="button"
                      onClick={() => {
                        setSelectedDate(d.iso);
                        setSelectedTime('');
                      }}
                      className={`flex flex-col items-center justify-center p-3 rounded-2xl min-w-17 transition-all border cursor-pointer active:scale-95 ${
                        isSelected
                          ? 'bg-brand text-white border-brand shadow-sm font-bold'
                          : !d.isOpen
                          ? 'bg-zinc-50 border-zinc-200/80 text-zinc-500 hover:bg-zinc-100/70'
                          : 'bg-white text-zinc-700 border-zinc-100 hover:bg-zinc-50'
                      }`}
                    >
                      <span className={`text-[10px] uppercase font-bold ${
                        isSelected 
                          ? 'text-white/80' 
                          : d.isToday 
                          ? 'text-brand font-black' 
                          : 'text-zinc-400'
                      }`}>
                        {d.isToday ? 'HOJE' : d.dayOfWeek}
                      </span>
                      <span className="text-base font-black my-0.5">
                        {d.dayNumber}
                      </span>
                      <span className={`text-[9px] font-bold ${
                        isSelected 
                          ? 'text-white/90' 
                          : !d.isOpen 
                          ? 'text-amber-700 bg-amber-50 px-1.5 py-0.2 rounded' 
                          : 'text-zinc-400'
                      }`}>
                        {!d.isOpen ? 'Esgotado' : (d.isToday ? d.dayOfWeek : d.month)}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Grid de Horários Livres (considerando bloco contínuo) */}
            <div>
              <label className="text-xs font-bold text-zinc-700 mb-2 flex items-center justify-between">
                <span>Horários Disponíveis ({selectedDate === getLocalDateString() ? 'Hoje' : new Date(selectedDate + 'T12:00:00').toLocaleDateString('pt-BR')}):</span>
                <span className="text-brand font-bold">{freeTimeSlots.length} livres</span>
              </label>

              {freeTimeSlots.length === 0 ? (
                <div className="p-5 bg-white border border-zinc-100 rounded-2xl text-center text-xs text-zinc-600 space-y-1">
                  <p className="font-bold text-zinc-800">
                    Agenda esgotada para esta data.
                  </p>
                  <p className="text-zinc-500 text-[11px]">
                    {!currentDaySchedule.isOpen
                      ? 'Não há vagas abertas na grade de atendimento para este dia. Por favor, selecione outra data disponível no calendário acima.'
                      : `Não encontramos intervalos livres de ${formatDurationDisplay(totalDurationMinutes)} nesta data. Por favor, escolha outro dia acima.`}
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {freeTimeSlots.map(time => {
                    const isSelected = selectedTime === time;
                    return (
                      <button
                        key={time}
                        type="button"
                        onClick={() => {
                          setSelectedTime(time);
                          setCurrentStep(4);
                        }}
                        className={`py-3 px-2 rounded-xl text-xs font-bold transition-all cursor-pointer border text-center flex items-center justify-center gap-1.5 active:scale-95 ${
                          isSelected
                            ? 'bg-brand text-white border-brand shadow-xs'
                            : 'bg-white text-zinc-800 border-zinc-100 hover:border-zinc-200 hover:bg-zinc-50'
                        }`}
                      >
                        <Clock className="h-3.5 w-3.5 opacity-60" />
                        {time}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* ────────────────────────────────────────────────────────────────── */}
        {/* PASSO 4: IDENTIFICAÇÃO DO CLIENTE & CONFIRMAÇÃO                    */}
        {/* ────────────────────────────────────────────────────────────────── */}
        {currentStep === 4 && (
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-4"
          >
            <div className="flex items-center justify-between">
              <button
                onClick={() => setCurrentStep(3)}
                className="text-xs font-bold text-zinc-500 hover:text-zinc-900 flex items-center gap-1 cursor-pointer"
              >
                <ChevronLeft className="h-4 w-4" /> Alterar horário
              </button>
              <h2 className="text-sm font-bold text-zinc-900">
                Seus Dados de Contato
              </h2>
            </div>

            {/* Resumo Card da Reserva com Itens Discriminados */}
            <div className="p-4 bg-zinc-50 border border-zinc-100 rounded-2xl space-y-2 text-xs text-zinc-700">
              <div className="pb-1.5 border-b border-zinc-200/80">
                <span className="text-[10px] text-zinc-400 uppercase font-black tracking-widest block mb-1">
                  Procedimentos Selecionados ({formatDurationDisplay(totalDurationMinutes)}):
                </span>
                <div className="space-y-1">
                  {selectedServices.map(s => (
                    <div key={s.id} className="flex justify-between items-center text-xs">
                      <span className="font-bold text-zinc-900">• {s.name} ({s.durationMinutes}min)</span>
                      <span className="font-bold text-zinc-700">{formatCurrencyBRL(s.price)}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-between">
                <span className="text-zinc-500">Data & Hora:</span>
                <span className="font-bold text-zinc-900">
                  {new Date(selectedDate + 'T12:00:00').toLocaleDateString('pt-BR')} às {selectedTime}
                </span>
              </div>

              {hasMultipleStaff && selectedStaff.id !== 'st_any' && (
                <div className="flex justify-between">
                  <span className="text-zinc-500">Profissional:</span>
                  <span className="font-bold text-zinc-900">{selectedStaff.name}</span>
                </div>
              )}

              <div className="pt-2 border-t border-zinc-200/80 flex justify-between items-center text-sm font-bold text-zinc-900">
                <span>Valor Total:</span>
                <span className="text-brand font-black text-base">
                  {formatCurrencyBRL(totalPrice)}
                </span>
              </div>
            </div>

            {/* Formulário de Identificação */}
            <form onSubmit={handleConfirmBooking} className="space-y-3.5">
              {/* Banner de Boas-Vindas se os dados foram auto-preenchidos */}
              {hasAutoFilled && clientName && (
                <div className="p-3 bg-emerald-50/90 border border-emerald-200 rounded-2xl flex items-center justify-between text-xs text-emerald-950 animate-fadeIn">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <span className="text-base shrink-0">👋</span>
                    <div className="min-w-0">
                      <p className="font-bold truncate">Olá, {clientName.split(' ')[0]}!</p>
                      <p className="text-[10px] text-emerald-700 font-medium">Seus dados foram preenchidos automaticamente.</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleClearAutoFill}
                    className="text-[11px] font-bold text-emerald-800 underline hover:text-emerald-950 cursor-pointer shrink-0 ml-2"
                  >
                    Alterar
                  </button>
                </div>
              )}

              {/* Botão de Preenchimento Nativo da Agenda (Android Chrome Contact Picker) */}
              {supportsContactPicker && !hasAutoFilled && (
                <button
                  type="button"
                  onClick={handlePickContact}
                  className="w-full p-2.5 bg-blue-50 hover:bg-blue-100/80 border border-blue-200 text-blue-800 rounded-xl text-xs font-bold flex items-center justify-center gap-2 transition-all cursor-pointer active:scale-95"
                >
                  <Contact className="h-4 w-4 text-blue-600" />
                  <span>Preencher com dados de contato</span>
                </button>
              )}

              <div>
                <label htmlFor="customer-name" className="text-[10px] text-zinc-400 uppercase font-black tracking-widest mb-1 block">
                  Seu Nome Completo: <span className="text-rose-500">*</span>
                </label>
                <div className="relative">
                  <User className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
                  <input
                    type="text"
                    id="customer-name"
                    name="name"
                    autoComplete="name"
                    autoCapitalize="words"
                    autoCorrect="off"
                    required
                    placeholder="Ex: Beatriz Lima"
                    value={clientName}
                    onChange={(e) => setClientName(e.target.value)}
                    className="w-full bg-zinc-50 border border-zinc-200 rounded-xl py-3 pl-10 pr-3.5 text-xs text-zinc-900 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-brand/20 font-medium transition-colors"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="customer-phone" className="text-[10px] text-zinc-400 uppercase font-black tracking-widest mb-1 block">
                  Seu WhatsApp: <span className="text-rose-500">*</span>
                </label>
                <div className="relative">
                  <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
                  <input
                    type="tel"
                    id="customer-phone"
                    name="tel"
                    autoComplete="tel"
                    inputMode="tel"
                    required
                    placeholder="(11) 98765-4321"
                    value={clientPhone}
                    onChange={handlePhoneChange}
                    className="w-full bg-zinc-50 border border-zinc-200 rounded-xl py-3 pl-10 pr-3.5 text-xs text-zinc-900 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-brand/20 font-medium transition-colors"
                  />
                </div>
                <p className="text-[11px] text-zinc-400 mt-1">
                  Seu telefone será utilizado para identificação no local.
                </p>
              </div>

              <div>
                <label className="text-[10px] text-zinc-400 uppercase font-black tracking-widest mb-1 block">
                  Observações (Opcional):
                </label>
                <textarea
                  rows={2}
                  placeholder="Alguma preferência ou observação para o atendimento?"
                  value={clientNotes}
                  onChange={(e) => setClientNotes(e.target.value)}
                  className="w-full bg-zinc-50 border border-zinc-200 rounded-xl p-3 text-xs text-zinc-900 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-brand/20 resize-none font-medium transition-colors"
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-3.5 bg-brand hover:bg-brand-dark text-white font-black text-xs uppercase tracking-wider rounded-xl shadow-sm shadow-brand/20 transition-all cursor-pointer active:scale-95 flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Confirmando seu horário...</span>
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="h-4 w-4" />
                    <span>Confirmar Agendamento ({selectedServices.length} {selectedServices.length === 1 ? 'item' : 'itens'})</span>
                  </>
                )}
              </button>
            </form>
          </motion.div>
        )}

        {/* ────────────────────────────────────────────────────────────────── */}
        {/* PASSO 5: SUCESSO LIMPO & CONFORTÁVEL                               */}
        {/* ────────────────────────────────────────────────────────────────── */}
        {currentStep === 5 && createdAppointment && (
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            className="space-y-4 py-2"
          >
            {/* Header de Confirmação */}
            <div className="text-center space-y-1.5">
              <div className="w-12 h-12 rounded-full bg-emerald-50 text-brand border border-emerald-200 flex items-center justify-center mx-auto shadow-xs">
                <Check className="h-6 w-6 stroke-3" />
              </div>
              <h2 className="text-lg font-bold text-zinc-900">
                Horário Confirmado!
              </h2>
              <p className="text-xs text-zinc-500">
                Seu atendimento está reservado com <strong className="text-zinc-800">{currentProfile.displayName}</strong>.
              </p>
            </div>

            {/* BOTÕES DE DESTAQUE: ADICIONAR À AGENDA COM AVISO ÀS 08:00 */}
            <div className="space-y-2">
              <button
                type="button"
                onClick={() => handleAddToCalendar('apple_ics')}
                className="w-full py-3.5 px-4 bg-brand hover:bg-brand-dark text-white rounded-2xl font-black text-xs uppercase tracking-wider flex items-center justify-center gap-2.5 shadow-md shadow-brand/25 cursor-pointer active:scale-98 transition-all"
              >
                <CalendarCheck className="h-4 w-4 stroke-[2.5]" />
                <span>Salvar na Agenda (Aviso às 08:00 do Dia)</span>
              </button>

              <div className="flex items-center justify-between gap-2">
                <button
                  type="button"
                  onClick={() => handleAddToCalendar('google')}
                  className="flex-1 py-2.5 px-3 bg-white hover:bg-zinc-50 text-zinc-800 border border-zinc-200 rounded-xl font-bold text-xs flex items-center justify-center gap-1.5 transition-all cursor-pointer shadow-2xs active:scale-95"
                >
                  <span>Google Calendar Web</span>
                </button>
                <div className="px-3 py-2 bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-xl font-bold text-[10px] flex items-center gap-1">
                  <span>⏰ Alarme: 08h00 + 45min</span>
                </div>
              </div>
            </div>

            {/* Resumo do Atendimento com Itens Discriminados */}
            <div className="bg-white border border-zinc-100 rounded-2xl p-4 space-y-2.5 shadow-sm text-xs">
              <div className="flex items-center justify-between pb-2 border-b border-zinc-100">
                <span className="font-black text-zinc-400 uppercase text-[10px] tracking-wider">
                  Resumo da Reserva ({createdAppointment.durationMinutes} min)
                </span>
                <span className="font-mono text-[11px] text-zinc-400 font-bold">
                  #{createdAppointment.id.slice(-6).toUpperCase()}
                </span>
              </div>

              <div className="space-y-2 text-zinc-700">
                <div className="space-y-1">
                  <span className="text-[10px] text-zinc-400 uppercase font-black block">Procedimentos:</span>
                  {createdAppointment.servicesList && createdAppointment.servicesList.length > 0 ? (
                    createdAppointment.servicesList.map(s => (
                      <div key={s.id} className="flex justify-between items-center">
                        <span className="font-bold text-zinc-900">• {s.name} ({s.durationMinutes}min)</span>
                        <span className="font-bold text-zinc-700">{formatCurrencyBRL(s.price)}</span>
                      </div>
                    ))
                  ) : (
                    <div className="flex justify-between">
                      <span className="font-bold text-zinc-900">{createdAppointment.serviceName}</span>
                    </div>
                  )}
                </div>

                {createdAppointment.staffMemberName && (
                  <div className="flex justify-between pt-1 border-t border-zinc-100">
                    <span className="text-zinc-500">Profissional:</span>
                    <span className="font-bold text-zinc-900">{createdAppointment.staffMemberName}</span>
                  </div>
                )}

                <div className="flex justify-between">
                  <span className="text-zinc-500">Data & Hora:</span>
                  <span className="font-bold text-zinc-900">
                    {new Date(createdAppointment.scheduledDate + 'T12:00:00').toLocaleDateString('pt-BR', { weekday: 'short', day: '2-digit', month: '2-digit' })} às {createdAppointment.scheduledTime}
                  </span>
                </div>

                <div className="flex justify-between items-center pt-2 border-t border-zinc-100">
                  <div>
                    <span className="font-bold text-zinc-900 block">Valor Total:</span>
                    <span className="text-[10px] text-zinc-400 font-medium">Pagamento no local após o serviço</span>
                  </div>
                  <span className="font-black text-brand text-base">
                    {formatCurrencyBRL(createdAppointment.price)}
                  </span>
                </div>
              </div>
            </div>

            {/* Localização & GPS Compacto com Ícones */}
            {currentProfile.addressDisplay && (
              <div className="bg-white border border-zinc-100 rounded-2xl p-3.5 flex items-center justify-between gap-3 shadow-xs">
                <div className="flex items-center gap-2.5 min-w-0">
                  <div className="w-8 h-8 rounded-xl bg-emerald-50 text-brand flex items-center justify-center shrink-0 border border-emerald-100">
                    <MapPin className="h-4 w-4" />
                  </div>
                  <div className="min-w-0">
                    <h4 className="text-xs font-bold text-zinc-900 leading-tight">Endereço do Local</h4>
                    <p className="text-[11px] text-zinc-500 truncate mt-0.5">{currentProfile.addressDisplay}</p>
                  </div>
                </div>

                <div className="flex items-center gap-1.5 shrink-0">
                  <button
                    type="button"
                    onClick={() => {
                      const url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(currentProfile.addressDisplay || '')}`;
                      window.open(url, '_blank');
                    }}
                    className="px-2.5 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 text-[11px] font-bold rounded-xl border border-blue-100 flex items-center gap-1 cursor-pointer active:scale-95 transition-all"
                    title="Abrir no Google Maps"
                  >
                    <Navigation className="h-3.5 w-3.5 text-blue-600" />
                    <span>Maps</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      const url = `https://waze.com/ul?q=${encodeURIComponent(currentProfile.addressDisplay || '')}`;
                      window.open(url, '_blank');
                    }}
                    className="px-2.5 py-1.5 bg-sky-50 hover:bg-sky-100 text-sky-700 text-[11px] font-bold rounded-xl border border-sky-100 flex items-center gap-1 cursor-pointer active:scale-95 transition-all"
                    title="Abrir no Waze"
                  >
                    <Navigation className="h-3.5 w-3.5 text-sky-500" />
                    <span>Waze</span>
                  </button>
                </div>
              </div>
            )}

            {/* Dúvidas ou Contato */}
            <div className="p-3.5 bg-emerald-50/60 border border-emerald-100 rounded-2xl flex items-center justify-between text-xs text-zinc-700">
              <span className="font-medium">Alguma dúvida sobre o atendimento?</span>
              <button
                type="button"
                onClick={() => {
                  const cleanPhone = currentProfile.whatsappNumber.replace(/\D/g, '');
                  const dateFmt = new Date(createdAppointment.scheduledDate + 'T12:00:00').toLocaleDateString('pt-BR');
                  const msg = `Olá, *${currentProfile.displayName}*! Tenho uma dúvida sobre o agendamento de *${createdAppointment.serviceName}* no dia ${dateFmt} às ${createdAppointment.scheduledTime}.`;
                  window.open(`https://api.whatsapp.com/send?phone=55${cleanPhone}&text=${encodeURIComponent(msg)}`, '_blank');
                }}
                className="font-black text-brand hover:text-brand-dark flex items-center gap-1.5 cursor-pointer bg-white px-3 py-1.5 rounded-xl border border-emerald-100 shadow-xs active:scale-95"
              >
                <MessageCircle className="h-3.5 w-3.5 text-brand" />
                <span>WhatsApp</span>
              </button>
            </div>

            {/* Ação Secundária */}
            <div className="text-center pt-2">
              <button
                type="button"
                onClick={() => {
                  setCurrentStep(1);
                  setSelectedServices([]);
                  setSelectedTime('');
                  setClientNotes('');
                  setCreatedAppointment(null);
                }}
                className="text-xs text-zinc-400 hover:text-zinc-700 transition-colors cursor-pointer font-medium"
              >
                Fazer outro agendamento
              </button>
            </div>
          </motion.div>
        )}

      </main>

      {/* Footer Minimalista e Limpo */}
      <footer className="py-4 border-t border-zinc-100 text-center text-[11px] text-zinc-400 bg-white">
        <span>Agendamento Online • Desenvolvido por <strong className="text-zinc-700">R-MAX</strong></span>
      </footer>

    </div>
  );
}
